<template>
  <!-- Exit button -->
  <button class="exit-game" @click="leaveGame">Exit Game</button>

  <div class="game-area">
    <!-- Cards on the table -->
    <div id="table">
      <!-- Players -->
      <PlayerCard
        v-for="(id, index) in sortedPlayers"
        :key="id"
        :player="players[id]"
        :player-id="id"
        :is-current-player="id === playerId"
        :user-name="userName"
        :current-player="currentPlayer"
        :can-play="canPlay"
        :position="getPlayerPosition(index, sortedPlayers.length)"
        :player-class="playerClasses[id]"
        :message="playerMessages[id]?.message"
        :loader-width="loaderWidth"
        :loader-value="loaderValue"
        :show-back="showBack(id)"
        :rounds-left="roundsLeft"
        :card-played="cardsPlayed[id]"
        :revealed-cards="revealedCards[id]"
        :show-buttons="showButtons(id)"
        :show-vuile-was-button="id === playerId && vuileWasButtonVisible"
        :show-check-button="
          vuileWasPlayers.includes(id) && players[id] && players[id].userName
        "
        :is-toeped="isToeped"
        :last-toeper="lastToeper"
        :would-reach-max="wouldReachMax"
        :toep-value="toepValue"
        :special-round="specialRound"
        :max-points="maxPoints"
        @toep="toep"
        @call="call"
        @fold="fold"
        @vuileWas="vuileWas"
        @playSpecialRound="playSpecialRound"
        @checkVuileWas="checkVuileWas"
      />
    </div>

    <!-- Player's hand -->
    <PlayerHand
      :player-hand="playerHand"
      :deal-id="dealId"
      :is-dimmed="playerClasses[playerId]?.dimmed"
      :can-play="canPlay"
      :user-name="userName"
      :current-player="currentPlayer"
      :is-toeped="isToeped"
      :table-cards="tableCards"
      :total-players="sortedPlayers.length"
      @playCard="playCard"
    />

    <!-- Vuile was indicator -->
    <CountdownTimer
      :visible="vuileWasCheck"
      :message="raised"
      :loader-width="loaderWidth"
      :loader-value="loaderValue"
    />
  </div>
</template>

<script>
import '@/styles/gameArea.css'
import { SocketManager } from '@/utility/SocketManager.js'
import storage from '@/utility/storage'
import PlayerCard from './PlayerCard.vue'
import PlayerHand from './PlayerHand.vue'
import CountdownTimer from './CountdownTimer.vue'

export default {
  name: 'GameArea',
  components: {
    PlayerCard,
    PlayerHand,
    CountdownTimer,
  },
  inject: ['socket'],
  props: {
    playerId: String,
    userName: String,
    roomId: String,
    maxPoints: Number,
  },
  data() {
    return {
      socketManager: null,
      dealId: 0, // Unique ID for the current deal
      playerHand: [], // player's hand
      players: {}, // players on the table
      tableCards: [], // cards on the table
      currentPlayer: null, // current player
      raised: null, // Placeholder for the toeper
      loaderWidth: 100, // Store the width of the loader
      totalCountdown: null, // Store the total countdown
      loaderValue: null, // Store the value of the loader
      countdownTimer: null, // Store interval ID
      countdownActive: false, // Track if countdown is currently active
      vuileWasPlayers: [], // Placeholder for the vuile was players
      vuileWasButtonVisible: false, // Placeholder for the vuile was button
      vuileWasCheck: false, // Placeholder for the vuile was check
      canPlay: true, // Placeholder for the play button
      isToeped: false, // Placeholder for the toep button
      lastToeper: null, // Placeholder for the last toeper
      toepValue: 1, // Placeholder for the toep value
      cardsPlayed: {}, // Store the cards played
      revealedCards: {}, // Store the revealed cards
      specialRound: false, // Store if it's a special round
      playerMessages: {}, // Store the player messages
      roundsLeft: 4, // Store the number of rounds left
    }
  },
  computed: {
    playerClasses() {
      const result = {}
      for (const id in this.players) {
        const isActive =
          (this.players[id]?.userName === this.currentPlayer && this.canPlay) ||
          this.vuileWasCheck

        const kleineSpeler =
          this.players[id].kleineSpeler === 2 &&
          !this.players[id].folded &&
          !this.specialRound

        result[id] = {
          dimmed: !isActive,
          kleineSpeler: kleineSpeler,
        }
      }
      return result
    },
    sortedPlayers() {
      const playerIds = Object.keys(this.players) || []
      const thisPlayerIndex = playerIds.indexOf(this.playerId)

      if (thisPlayerIndex === -1) {
        return playerIds // Default order if player not found
      }

      // Rotate array so current player is first
      return [
        ...playerIds.slice(thisPlayerIndex),
        ...playerIds.slice(0, thisPlayerIndex),
      ]
    },
    wouldReachMax() {
      return Object.values(this.players).some((player) => {
        if (!player || player.points === undefined || player.folded) {
          return false
        }

        let wouldReachMax = player.points + this.toepValue >= this.maxPoints
        return wouldReachMax
      })
    },
  },
  methods: {
    showButtons(id) {
      return (
        id === this.playerId &&
        this.userName === this.currentPlayer &&
        this.canPlay &&
        !this.vuileWasButtonVisible &&
        !this.specialRound
      )
    },
    showBack(id) {
      return (
        id !== this.playerId &&
        !this.revealedCards[id] &&
        !this.players[id].folded
      )
    },
    updateLoader(value) {
      if (!this.totalCountdown || value > this.totalCountdown) {
        this.totalCountdown = value
      }
      this.loaderValue = value
      this.loaderWidth = (value / this.totalCountdown) * 100
    },
    startClientCountdown(endTime, duration, sort) {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }

      // Mark countdown as active
      this.countdownActive = true

      // Set total countdown if not already set or if new duration is larger
      if (!this.totalCountdown || duration > this.totalCountdown) {
        this.totalCountdown = duration
      }

      const updateCountdown = () => {
        const now = Date.now()
        const remaining = Math.max(0, Math.floor((endTime - now) / 1000))

        this.updateLoader(remaining)

        // Update UI based on sort type
        switch (sort) {
          case 'special':
            this.raised = `Special round starting in ${remaining}`
            break
          case '1st':
            this.raised = `1st check over in ${remaining}`
            break
          case '2nd':
            this.raised = `2nd check over in ${remaining}`
            this.vuileWasButtonVisible = false
            break
          case 'turn':
            this.canPlay = true
            this.raised = `${
              this.currentPlayer === this.userName
                ? `You have`
                : `${this.currentPlayer} has`
            } ${remaining} seconds left`
            break
        }

        if (remaining <= 0) {
          clearInterval(this.countdownTimer)
          this.countdownTimer = null
          this.countdownActive = false
        }
      }

      // Update immediately
      updateCountdown()

      // Then update every second
      this.countdownTimer = setInterval(updateCountdown, 1000)
    },
    getPlayerPosition(index, totalPlayers) {
      // Base angle calculation
      let angle = (index / totalPlayers) * 2 * Math.PI

      // Adjust angles for side players (index 2 and 5 for 6 players)
      if (totalPlayers === 6) {
        if (index === 1) {
          angle -= 0.3 // Move slightly inward
        } else if (index === 2) {
          angle += 0.3 // Move slightly inward
        } else if (index === 4) {
          angle -= 0.3 // Move slightly inward
        } else if (index === 5) {
          angle += 0.3 // Move slightly inward
        }
      } else if (totalPlayers === 7) {
        if (index === 1 || index === 5) {
          angle -= 0.2 // Move slightly inward
        } else if (index === 3) {
          angle += 0.1 // Move slightly inward
        } else if (index === 4) {
          angle -= 0.1 // Move slightly inward
        } else if (index === 6 || index === 2) {
          angle += 0.2 // Move slightly inward
        }
      } else if (totalPlayers === 8) {
        if (index === 1) {
          angle -= 0.2 // Move slightly inward
        } else if (index === 3) {
          angle += 0.2 // Move slightly inward
        } else if (index === 5) {
          angle -= 0.2 // Move slightly inward
        } else if (index === 7) {
          angle += 0.2 // Move slightly inward
        }
      }

      // Center of the table
      const centerX = 50 // 50% (relative to parent)
      const centerY = 50 // 50% (relative to parent)

      // Radius from center
      const radiusX = 48 // Adjust for oval shape
      const radiusY = 40 // Adjust for oval shape

      // Calculate position using sine & cosine
      const x = centerX - radiusX * Math.sin(angle)
      const y = centerY + radiusY * Math.cos(angle)

      return {
        position: 'absolute',
        left: `${x}%`,
        top: `${y}%`,
        transform: 'translate(-50%, -50%)', // Center the player div
        textAlign: 'center',
      }
    },

    playCard(card) {
      if (!this.canPlay) {
        console.log('Can not yet play!')
        return
      }
      // check if it's players turn
      if (this.userName !== this.currentPlayer) {
        console.log('Not your turn!')
        return
      }
      // check if player has suit but not played
      if (this.tableCards.length > 0) {
        let suit = this.tableCards[0].card.suit
        let hasSuit = this.playerHand.some((c) => c.suit === suit)
        if (hasSuit && card.suit !== suit) {
          console.log('You must play the same suit!')
          return
        }
      }

      // Emit the updated state to the server
      this.socketManager.emitSocketEvent('playCard', {
        roomId: this.roomId,
        playerId: this.playerId,
        card: card,
      })
    },

    toep() {
      if (!this.canPlay) {
        console.log('Can not yet play!')
        return
      }
      this.socketManager.emitSocketEvent('toep', {
        roomId: this.roomId,
        playerId: this.playerId,
      })
    },

    call() {
      if (!this.canPlay) {
        console.log('Can not yet play!')
        return
      }
      this.socketManager.emitSocketEvent('call', {
        roomId: this.roomId,
        playerId: this.playerId,
      })
    },

    fold() {
      if (!this.canPlay) {
        console.log('Can not yet play!')
        return
      }
      this.socketManager.emitSocketEvent('fold', {
        roomId: this.roomId,
        playerId: this.playerId,
      })
    },

    vuileWas() {
      this.socketManager.emitSocketEvent('vuileWas', {
        roomId: this.roomId,
        playerId: this.playerId,
        playerHand: this.playerHand,
      })
      this.vuileWasButtonVisible = false
    },

    checkVuileWas(playerId) {
      // check if player checks himself
      if (playerId === this.playerId) {
        console.log('You can not check yourself!')
        return
      }

      this.socketManager.emitSocketEvent('checkVuileWas', {
        roomId: this.roomId,
        playerId: this.playerId,
        vuileWasPlayerId: playerId,
      })

      // remove player from vuileWasPlayers
      this.vuileWasPlayers = this.vuileWasPlayers.filter((p) => p !== playerId)
    },

    playSpecialRound() {
      this.socketManager.emitSocketEvent('playSpecialRound', {
        roomId: this.roomId,
        playerId: this.playerId,
      })
      this.vuileWasButtonVisible = false
    },

    leaveGame() {
      console.log('Leaving game...')
      this.$router.push('/lobby') // Redirect to the lobby
      this.socketManager.emitSocketEvent('leaveGame', {
        playerId: this.playerId,
        roomId: this.roomId,
      }) // Emit the leaveRoom event

      // Clear the session storage
      storage.remove('playerId')
      storage.remove('playerHand')
      storage.remove('gameId')
      storage.remove('userName')
    },

    showPlayerMessage(playerId, message) {
      if (!playerId || !message) {
        return
      }

      if (this.playerMessages[playerId]?.timeout) {
        clearTimeout(this.playerMessages[playerId].timeout)
      }

      const timeout = setTimeout(() => {
        if (this.playerMessages[playerId]) {
          delete this.playerMessages[playerId]
        }
      }, 3000)

      this.playerMessages[playerId] = { message, timeout }
    },
  },
  mounted() {
    this.socketManager = new SocketManager(this.socket)

    this.socketManager.emitSocketEvent(
      'connected',
      { playerId: this.playerId, roomId: this.roomId },
      (res) => {
        if (res) {
          this.playerHand = JSON.parse(storage.get('playerHand')) || []
        } else {
          console.error('Connection failed!')
        }
      }
    )

    this.socketManager.onSocketEvent('connect', () => {
      this.socketManager.emitSocketEvent(
        'connected',
        { playerId: this.playerId, roomId: this.roomId },
        (res) => {
          if (res) {
            this.playerHand = JSON.parse(storage.get('playerHand')) || []
          } else {
            console.error('Connection failed!')
          }
        }
      )
    })

    this.socketManager.onSocketEvent('updatePlayers', (players) => {
      this.players = players
      this.$emit('updatePlayers', players)
    })

    // Register the listener for the 'cardsDealt' event
    this.socketManager.onSocketEvent('cardsDealt', (cards, playerId) => {
      console.log('cards dealt')
      if (this.revealedCards[playerId]) {
        delete this.revealedCards[playerId]
      }

      if (playerId === this.playerId) {
        this.dealId = Date.now() // or a simple incrementing counter
        this.playerHand = cards

        // Save the player's hand to the session storage
        storage.set('playerHand', JSON.stringify(cards))
      }
    })

    this.socketManager.onSocketEvent('saysVuileWas', (playerId) => {
      if (this.playerId !== playerId) {
        this.vuileWasPlayers.push(playerId)
      }

      let message = 'Vuile Was!'
      this.showPlayerMessage(playerId, message)
    })

    this.socketManager.onSocketEvent('vuileWasStarted', () => {
      this.canPlay = false
      this.vuileWasButtonVisible = true
      this.vuileWasCheck = true
    })

    this.socketManager.onSocketEvent('vuileWasFinished', () => {
      this.vuileWasPlayers = []
      this.vuileWasButtonVisible = false
      this.vuileWasCheck = false
    })

    this.socketManager.onSocketEvent(
      'countdownStarted',
      ({ endTime, duration, sort }) => {
        this.startClientCountdown(endTime, duration, sort)
      }
    )

    this.socketManager.onSocketEvent(
      'countdownSync',
      ({ endTime, remaining, sort }) => {
        if (!this.countdownActive) {
          return
        }

        const clientRemaining = Math.floor((endTime - Date.now()) / 1000)
        if (Math.abs(clientRemaining - remaining) > 1) {
          const duration = Math.floor((endTime - Date.now()) / 1000)
          if (duration > 0) {
            this.startClientCountdown(endTime, duration, sort)
          }
        }
      }
    )

    this.socketManager.onSocketEvent('countdownStopped', () => {
      this.canPlay = false
      this.countdownActive = false
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    })

    this.socketManager.onSocketEvent(
      'revealCards',
      (playerId, vuileWasPlayerId, vuileWas) => {
        if (playerId === this.playerId) {
          console.log('Revealing cards...')
          this.revealedCards[vuileWasPlayerId] = vuileWas
        }
      }
    )

    this.socketManager.onSocketEvent('setTableCards', (tableCards) => {
      this.tableCards = tableCards

      tableCards.forEach(({ playerId, card }) => {
        if (!this.players[playerId]) {
          return
        }
        this.cardsPlayed[playerId] = card
      })
    })

    this.socketManager.onSocketEvent('setToepValue', (value, lastToeper) => {
      this.toepValue = value
      this.lastToeper = lastToeper
    })

    this.socketManager.onSocketEvent('slagWon', (winnerId) => {
      this.canPlay = false
      this.raised = `${this.players[winnerId]?.userName} wins!`

      setTimeout(() => {
        this.tableCards = []
        this.cardsPlayed = {}
        this.roundsLeft--
      }, 2000) // Delay in milliseconds (1000ms = 1 second)
    })

    this.socketManager.onSocketEvent('currentPlayer', (userName) => {
      console.log('current player', userName)
      this.currentPlayer = userName // Update the current player index
    })

    this.socketManager.onSocketEvent('cardPlayed', (card, tableCards) => {
      this.isToeped = false

      // Remove the card from the player's hand and add it to the table
      this.playerHand = this.playerHand.filter(
        (c) => c.value !== card.card.value || c.suit !== card.card.suit
      )

      if (
        this.revealedCards[card.playerId] &&
        this.revealedCards[card.playerId].length > 0
      ) {
        this.revealedCards[card.playerId] = this.revealedCards[
          card.playerId
        ].filter(
          (c) => c.value !== card.card.value || c.suit !== card.card.suit
        )
      }

      this.cardsPlayed[card.playerId] = card.card
      this.tableCards = tableCards

      // Save the player's hand to the session storage
      storage.set('playerHand', JSON.stringify(this.playerHand))
    })

    this.socketManager.onSocketEvent('toeped', (playerId, toepValue) => {
      let userName = this.players[playerId]?.userName
      this.showPlayerMessage(playerId, `Ik toep!`)

      this.lastToeper = userName
      this.toepValue = toepValue

      if (userName !== this.userName) {
        this.isToeped = true
      }
    })

    this.socketManager.onSocketEvent('called', (playerId) => {
      this.showPlayerMessage(playerId, `Ik call!`)
    })

    this.socketManager.onSocketEvent('folded', (playerId) => {
      this.showPlayerMessage(playerId, `Ik fold!`)
    })

    this.socketManager.onSocketEvent('roundEnded', (winnerId, isJack) => {
      this.canPlay = false
      this.countdownActive = false
      let winner = this.players[winnerId]?.userName
      this.raised = `${winner} wins${isJack ? ' with jack' : ''}!`
      this.showPlayerMessage(
        winnerId,
        `Ik won${isJack ? ' met een Boer!' : '!'}`
      )

      this.lastToeper = ''
      this.isToeped = false
      storage.set('playerHand', JSON.stringify([]))
      console.log('New round started!')
    })

    this.socketManager.onSocketEvent('newRound', () => {
      this.totalCountdown = null
      this.canPlay = false
      this.countdownActive = false
      this.lastToeper = ''
      this.isToeped = false
      this.toepValue = 1
      this.playerHand = []
      this.tableCards = []
      this.cardsPlayed = {}
      this.revealedCards = {}
      this.specialRound = false
      this.roundsLeft = 4

      // Clean up all talk bubble timeouts
      for (const playerId in this.playerMessages) {
        const timeout = this.playerMessages[playerId]?.timeout
        if (timeout) clearTimeout(timeout)
      }
      this.playerMessages = {}

      // Clean up all countdown timers
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    })

    this.socketManager.onSocketEvent('specialRoundStarted', () => {
      this.canPlay = false
      this.specialRound = true
      this.vuileWasButtonVisible = true
      this.vuileWasCheck = true
    })

    this.socketManager.onSocketEvent('specialRoundFinished', () => {
      this.vuileWasButtonVisible = false
      this.vuileWasCheck = false
    })

    this.socketManager.onSocketEvent('gameOver', (score) => {
      console.log('Game over! Losers:', score)
      this.$router.push({
        path: `/gameover/${this.roomId}`,
        query: { score: encodeURIComponent(JSON.stringify(score)) },
      })

      // Clear the session storage
      storage.remove('playerId')
      storage.remove('playerHand')
      storage.remove('gameId')
      storage.remove('userName')
      this.socketManager.emitSocketEvent('deleteRoom', {
        roomId: this.roomId,
      })
    })

    this.socketManager.onSocketEvent('talkCloud', (playerId, message) => {
      if (!playerId || !message) {
        return
      }

      this.showPlayerMessage(playerId, message)
    })
  },
  beforeUnmount() {
    // Remove the listener to prevent memory leaks when the component is destroyed
    this.socketManager.offSocketEvent('connect')
    this.socketManager.offSocketEvent('reconnected')
    this.socketManager.offSocketEvent('updatePlayers')
    this.socketManager.offSocketEvent('cardsDealt')
    this.socketManager.offSocketEvent('saysVuileWas')
    this.socketManager.offSocketEvent('vuileWasStarted')
    this.socketManager.offSocketEvent('vuileWasFinished')
    this.socketManager.offSocketEvent('countdownStarted')
    this.socketManager.offSocketEvent('countdownSync')
    this.socketManager.offSocketEvent('countdownStopped')
    this.socketManager.offSocketEvent('revealCards')
    this.socketManager.offSocketEvent('setTableCards')
    this.socketManager.offSocketEvent('setToepValue')
    this.socketManager.offSocketEvent('slagWon')
    this.socketManager.offSocketEvent('currentPlayer')
    this.socketManager.offSocketEvent('cardPlayed')
    this.socketManager.offSocketEvent('toeped')
    this.socketManager.offSocketEvent('called')
    this.socketManager.offSocketEvent('folded')
    this.socketManager.offSocketEvent('roundEnded')
    this.socketManager.offSocketEvent('newRound')
    this.socketManager.offSocketEvent('specialRoundStarted')
    this.socketManager.offSocketEvent('specialRoundFinished')
    this.socketManager.offSocketEvent('gameOver')
    this.socketManager.offSocketEvent('talkCloud')
    clearInterval(this.countdownTimer)
  },
}
</script>